/**
 * Coercions for values Recharts hands to a tooltip formatter.
 *
 * Recharts types a tooltip value as `number | string | ReadonlyArray<number |
 * string>`, optionally undefined — it has to, because a formatter is also
 * reachable from stacked and range series where the datum is a pair. Our charts
 * are all single numeric series, so the wide cases never arrive at runtime, but
 * a formatter annotated `(value: number)` is still lying to the compiler about
 * a callback it does not control.
 *
 * These narrow it in one place rather than at each of the six call sites, so a
 * chart that does start carrying an array degrades to a readable dash instead of
 * rendering the string "NaN" at a customer.
 */

/** What a Recharts tooltip formatter may be handed for a value. */
type ChartValue = number | string | ReadonlyArray<number | string> | undefined;

/** What it may be handed for a series name. */
type ChartName = number | string | undefined;

/**
 * Reduce a tooltip value to the number the formatters expect.
 *
 * Returns null rather than NaN for anything that is not a finite number, so the
 * callers have something they can branch on — `formatCurrency(NaN)` renders
 * "$NaN", which is the failure this exists to prevent.
 */
export function chartNumber(value: ChartValue): number | null {
  /* An array is a stacked or range datum. Its first entry is the one the
     tooltip row is describing; the rest belong to other rows. */
  const scalar = Array.isArray(value) ? value[0] : value;
  if (scalar === undefined || scalar === null || scalar === '') return null;

  const parsed = Number(scalar);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Reduce a tooltip series name to a string, since Recharts allows a number. */
export function chartName(name: ChartName): string {
  return name === undefined || name === null ? '' : String(name);
}

export type { ChartValue, ChartName };
